/**
 * Pre-Built Configuration types
 * Mirrors the configurations table schema from migration 279.
 * See 10A (definitions) and 10C (schema).
 */

export type StructuralFootprint = 'zero' | 'minimal' | 'light' | 'moderate' | 'full';
export type CatchballApplicability = 'none' | 'optional' | 'required';
export type WorkflowRequired = 'none' | 'workspace_default' | 'explicit';
export type ObjectType = 'activities' | 'people' | 'places' | 'things' | 'information' | 'time';
export type OutputShape = 'ranking' | 'allocation' | 'sequencing' | 'filtering' | 'matching';
export type FailureMode = 'forgetting' | 'friction' | 'drift';
export type PersistenceDestination = 'personal' | 'group' | 'cycle';

export type ConfigurationStatus = 'active' | 'archived';

/** The slugs for all ten system configurations — do not change these. */
export type SystemConfigurationSlug =
  | 'individual-brain-dump'
  | 'team-meeting'
  | 'offsite-planning'
  | 'strategic-planning'
  | 'resource-allocation'
  | 'hiring-talent'
  | 'vendor-partner-selection'
  | 'portfolio-management'
  | 'personal-team-focus'
  | 'enterprise-prioritization-cycle';

/** The jsonb spec stored in configurations.configuration */
export interface ConfigurationSpec {
  entry_point: string;
  framing_depth: string;
  decision_structure: string;
  default_tool_recommendations: string[];
  promotion_path: string;
  notes?: string;
  invariants?: string[];
}

/** Full configuration record as returned from Supabase */
export interface Configuration {
  id: string;
  workspace_id: string | null; // null = system configuration
  name: string;
  slug: string;
  description: string | null;
  is_system: boolean;
  status: ConfigurationStatus;
  structural_footprint: StructuralFootprint;
  catchball_applicability: CatchballApplicability;
  workflow_required: WorkflowRequired;
  cycle_template_used: boolean;
  primary_failure_modes: FailureMode[];
  object_types: ObjectType[];
  output_shapes: OutputShape[];
  persistence_destination: PersistenceDestination;
  configuration: ConfigurationSpec;
  created_at: string;
  created_by: string | null;
  updated_at: string;
}

/** Human-readable label for structural footprint */
export const FOOTPRINT_LABELS: Record<StructuralFootprint, string> = {
  zero: 'Zero setup',
  minimal: 'Minimal',
  light: 'Light',
  moderate: 'Moderate',
  full: 'Full',
};

/** Human-readable label for failure modes */
export const FAILURE_MODE_LABELS: Record<FailureMode, string> = {
  forgetting: 'Prevents Forgetting',
  friction: 'Reduces Friction',
  drift: 'Stops Drift',
};

/** Human-readable description of failure modes */
export const FAILURE_MODE_DESCRIPTIONS: Record<FailureMode, string> = {
  forgetting: 'Skipping upstream stages like participation or pool formation',
  friction: 'Setup overhead that causes teams to shortcut the process',
  drift: 'Criteria, weights, and pools changing informally across cycles',
};

/** What structural footprint implies about whether a cycle is needed */
export function footprintRequiresCycle(footprint: StructuralFootprint): boolean {
  return footprint === 'moderate' || footprint === 'full';
}

/** Whether a configuration can go directly to session creation without a cycle */
export function configurationCanSkipCycle(config: Configuration): boolean {
  return config.workflow_required === 'none' &&
    (config.structural_footprint === 'zero' || config.structural_footprint === 'minimal');
}
